<script setup>
import { computed, inject } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { SparklesIcon } from '@heroicons/vue/solid'

import { useAuth } from '@/Hooks/Company/useAuth'
import { useFeatureFlags } from '@/Hooks/Company/useFeatureFlags'

const props = defineProps({
  variant: {
    type: String,
    default: 'desktop',
    validator: (value) => ['desktop', 'mobile'].includes(value),
  },
})

const openAiChat = inject('openAiChat', () => {})
const { authCompanyTeamMember } = useAuth()
const { hasFeatureFlag } = useFeatureFlags()

const permissionNames = computed(
  () => authCompanyTeamMember.value?.permissions?.map((permission) => permission.name) ?? []
)

const hasAiChatAccess = computed(
  () =>
    authCompanyTeamMember.value?.ai_chat_access === true ||
    authCompanyTeamMember.value?.has_ai_chat_access === true ||
    permissionNames.value.includes('ai_chat_access')
)

const page = usePage()

const shouldRender = computed(() => {
  // The backend gate denies everything when no AI provider is configured, so
  // hide the entry point entirely rather than exposing a dead launcher.
  if (page.props.aiChatProviderConfigured === false) {
    return false
  }

  if (!hasFeatureFlag('ai_chat')) {
    return false
  }

  const teamMember = authCompanyTeamMember.value

  if (!teamMember) {
    return false
  }

  if (teamMember.ai_chat_access === false || teamMember.has_ai_chat_access === false) {
    return false
  }

  return hasAiChatAccess.value
})

const buttonClass = computed(() => {
  if (props.variant === 'mobile') {
    return 'block text-black hover:text-header-off-white-dark focus:outline-none focus-visible:shadow-focus'
  }

  return 'flex items-center h-10 max-w-xs px-3 text-sm rounded-xl focus:outline-none focus-visible:shadow-focus'
})
</script>

<template>
  <button
    v-if="shouldRender"
    type="button"
    :class="buttonClass"
    title="Open Aryeo Assistant"
    @click="openAiChat"
  >
    <span class="sr-only">Open Aryeo Assistant</span>
    <SparklesIcon
      class="block text-black shrink-0"
      :class="variant === 'mobile' ? 'w-6 h-6' : 'w-5 h-5'"
      aria-hidden="true"
    />
  </button>
</template>
